import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, FileText } from 'lucide-react';
type Status = 'Completed' | 'No Content' | 'Workers Signing';
interface Record {
  id: string;
  fileType: 'pdf' | 'missing';
  fileName: string;
  site: string;
  subName: string;
  date: string;
  status: Status;
}
const SITES = ['1760 3rd Ave', '1760 Jerome Ave', 'Court Square LIC', '99 Claremont Ave', '251 Douglass Street', '450 Park Avenue South', '520 Fifth Avenue'];
const SUBS = ['Aurora Paint Corp.', 'Skytrac', 'Flushing Iron Weld', 'Sector Security', 'Luxintec', 'V10 Construction', 'Kitchens Of USA', 'Otis Elevator Company', 'Sunkeeper Solar', 'Architectural Flooring Resource Inc'];
function generateMockData(): Record[] {
  const data: Record[] = [];
  let id = 1;
  const add = (date: string, status: Status, subIdx: number, siteIdx: number) => {
    const isCompleted = status === 'Completed';
    data.push({
      id: String(id++),
      fileType: isCompleted ? 'pdf' : status === 'Workers Signing' ? 'pdf' : 'missing',
      fileName: isCompleted ? 'Signed PTP' : '',
      site: SITES[siteIdx % SITES.length],
      subName: SUBS[subIdx % SUBS.length],
      date,
      status
    });
  };
  // Today (03/15)
  for (let i = 0; i < 5; i++) add('03/15/2026', 'No Content', i, i);
  for (let i = 0; i < 3; i++) add('03/15/2026', 'Workers Signing', i + 2, i + 1);
  for (let i = 0; i < 10; i++) add('03/15/2026', 'Completed', i + 4, i + 2);
  // Last 7 days (03/09 - 03/14)
  for (let i = 0; i < 15; i++) add('03/12/2026', 'No Content', i + 1, i + 3);
  for (let i = 0; i < 10; i++) add('03/11/2026', 'Workers Signing', i + 3, i + 4);
  for (let i = 0; i < 30; i++) add('03/10/2026', 'Completed', i + 5, i + 5);
  return data;
}
const MOCK_DATA = generateMockData();
export function PTPPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [periodLabel, setPeriodLabel] = useState<string | null>(null);
  const [siteFilter, setSiteFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{
    start: string | null;
    end: string | null;
  }>({
    start: null,
    end: null
  });
  useEffect(() => {
    const period = searchParams.get('period');
    const status = searchParams.get('status');
    const site = searchParams.get('site');
    if (status === 'incomplete') {
      setStatusFilter('Incomplete');
    } else {
      setStatusFilter(null);
    }
    if (site) {
      setSiteFilter(site);
    } else {
      setSiteFilter(null);
    }
    if (period === 'today') {
      setPeriodLabel('Today');
      setDateRange({
        start: '03/15/2026',
        end: '03/15/2026'
      });
    } else if (period === 'week') {
      setPeriodLabel('Last 7 days');
      setDateRange({
        start: '03/09/2026',
        end: '03/15/2026'
      });
    } else {
      setPeriodLabel(null);
      setDateRange({
        start: null,
        end: null
      });
    }
  }, [searchParams]);
  const clearAllFilters = () => {
    setStatusFilter(null);
    setPeriodLabel(null);
    setSiteFilter(null);
    setDateRange({
      start: null,
      end: null
    });
    setSearchParams({});
  };
  const removeFilter = (key: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(key);
    setSearchParams(newParams);
  };
  const filteredData = useMemo(() => {
    return MOCK_DATA.filter(item => {
      if (searchQuery && !item.subName.toLowerCase().includes(searchQuery.toLowerCase()) && !item.site.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (statusFilter === 'Incomplete') {
        if (item.status === 'Completed') return false;
      } else if (statusFilter && item.status !== statusFilter) {
        return false;
      }
      if (siteFilter && item.site !== siteFilter) {
        return false;
      }
      if (dateRange.start && item.date < dateRange.start) return false;
      if (dateRange.end && item.date > dateRange.end) return false;
      return true;
    });
  }, [searchQuery, statusFilter, dateRange, siteFilter]);
  const hasActiveFilters = statusFilter !== null || dateRange.start !== null || siteFilter !== null;
  return <div className="min-h-screen bg-[#fafafa] font-sans flex flex-col" data-id="element-4399" data-mp="3i5d59u16tx1h">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4" data-id="element-4400" data-mp="3i3ikcfpx24xe">
        <div className="flex items-center gap-2" data-id="element-4401" data-mp="3n57vdvd52ilh">
          <div className="text-blue-600 font-bold text-2xl tracking-tighter flex items-center" data-id="element-4402" data-mp="3s42p3scn9rkd">
            CHST
            <div className="w-6 h-6 border-4 border-blue-600 rounded-full mx-0.5 relative" data-id="element-4403" data-mp="1sr6znjcvybpm">
              <div className="absolute inset-0 m-auto w-2 h-2 bg-blue-600 rounded-full" data-id="element-4404" data-mp="1xoqb6bi78adv"></div>
            </div>
          </div>
          <div className="text-[10px] font-bold text-blue-600 tracking-widest absolute mt-8 ml-1" data-id="element-4405" data-mp="ev6wkvntj3vg">
            L D E R S
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 ml-4" data-id="element-4406" data-mp="623geflsgcx4">PTP</h1>
      </div>

      <main className="flex-1 p-6 max-w-[1600px] mx-auto w-full" data-id="element-4407" data-mp="5zpjs2bgmsng">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-4408" data-mp="14k0lb9fzs32">
          <h2 className="text-xl text-gray-700" data-id="element-4409" data-mp="xryhtamx7t25">
            Found{' '}
            <span className="font-semibold text-gray-900" data-id="element-4410" data-mp="treic0evkdcu">
              {filteredData.length}
            </span>{' '}
            PTPs
          </h2>

          <div className="flex items-center gap-3" data-id="element-4411" data-mp="ouo47immrru2">
            <div className="relative" data-id="element-4412" data-mp="ju9snqltlzra">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-4413" data-mp="jt8evedf6z5v" />
              <input type="text" placeholder="Search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4414" data-mp="34j68jott40ce" />
            </div>
            <button className="relative flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 bg-white transition-colors shadow-sm" data-id="element-4415" data-mp="31z19ipjyo5f9">
              <SlidersHorizontal size={16} data-id="element-4416" data-mp="2s0tuwdd59zgf" />
              Filters
              {hasActiveFilters && <span className="absolute top-0 right-0 -mt-1 -mr-1 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" data-id="element-4417" data-mp="2ea8jf9q0srsk"></span>}
            </button>
          </div>
        </div>

        {hasActiveFilters && <div className="flex items-center flex-wrap gap-2 mb-6" data-id="element-4418" data-mp="24cu6vl77wn5j">
            {siteFilter && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4419" data-mp="299au8d6lexhk">
                <span className="text-gray-300" data-id="element-4420" data-mp="1pp2ex6zzvzh4">Site:</span>
                <span className="font-medium" data-id="element-4421" data-mp="1kqiu59x50tqc">{siteFilter}</span>
                <button onClick={() => removeFilter('site')} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4422" data-mp="1zn035uhutw9q">
                  <X size={14} data-id="element-4423" data-mp="1upzyno0nl1kq" />
                </button>
              </div>}

            {statusFilter && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4424" data-mp="23olevcink4ic">
                <span className="text-gray-300" data-id="element-4425" data-mp="2ie0ym8jrn9oj">Status:</span>
                <span className="font-medium" data-id="element-4426" data-mp="2ifkehy1ifips">{statusFilter}</span>
                <button onClick={() => removeFilter('status')} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4427" data-mp="2dfpodwpk6x5s">
                  <X size={14} data-id="element-4428" data-mp="2ndlzioihxs3w" />
                </button>
              </div>}

            {periodLabel && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4429" data-mp="3tr0jtoukcqxr">
                <span className="text-gray-300" data-id="element-4430" data-mp="2jedfoggmad7">Period:</span>
                <span className="font-medium" data-id="element-4431" data-mp="7lde2qktet6g">{periodLabel}</span>
                <span className="text-gray-400 text-xs" data-id="element-4432" data-mp="blxdy1wrwawa">
                  ({dateRange.start} – {dateRange.end})
                </span>
                <button onClick={() => removeFilter('period')} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4433" data-mp="3b23odhnfjsdy">
                  <X size={14} data-id="element-4434" data-mp="3k1h06gz10t1u" />
                </button>
              </div>}

            <button onClick={clearAllFilters} className="text-sm text-gray-500 hover:text-gray-900 underline underline-offset-2 ml-2 transition-colors" data-id="element-4435" data-mp="3osz2p9gp71ox">
              Clear all
            </button>
          </div>}

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-4436" data-mp="2wa1ss9se7ljq">
          <div className="overflow-x-auto" data-id="element-4437" data-mp="2oqj3jlsk2w2p">
            <table className="w-full text-left text-sm" data-id="element-4438" data-mp="2jtakzk3hwq0x">
              <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 font-semibold text-xs tracking-wider" data-id="element-4439" data-mp="2jrg20bbsb7zh">
                <tr data-id="element-4440" data-mp="2at4am733g65z">
                  <th className="px-6 py-4" data-id="element-4441" data-mp="2atng2obk5234">FILE</th>
                  <th className="px-6 py-4" data-id="element-4442" data-mp="2auyox2jv1013">SITE</th>
                  <th className="px-6 py-4" data-id="element-4443" data-mp="25ut0dv8vztn8">SUB NAME</th>
                  <th className="px-6 py-4" data-id="element-4444" data-mp="213191ihwpjw1">DATE</th>
                  <th className="px-6 py-4" data-id="element-4445" data-mp="2129d26kedk58">STATUS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-4446" data-mp="1w4rbx2cmg97s">
                {filteredData.map(row => <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-id="element-4447" data-mp="1s3xosb706yfy">
                    <td className="px-6 py-4" data-id="element-4448" data-mp="1i46rk1r265c6">
                      <div className="flex items-center gap-3" data-id="element-4449" data-mp="1i5hww605jl5e">
                        {row.fileType === 'pdf' ? <div className="relative" data-id="element-4450" data-mp="3tpv4ggj999ht">
                            <FileText size={24} className="text-gray-300" data-id="element-4451" data-mp="3pqm771p0athi" />
                            <div className="absolute bottom-0 left-0 bg-red-600 text-white text-[8px] font-bold px-1 rounded-sm" data-id="element-4452" data-mp="7c3k3q88bjzi">
                              PDF
                            </div>
                          </div> : <div className="relative" data-id="element-4453" data-mp="3atbzm3fhi7r2">
                            <FileText size={24} className="text-gray-200" data-id="element-4454" data-mp="36rqgw1g9qz8y" />
                            <div className="absolute bottom-0 left-0 bg-gray-500 text-white text-[8px] font-bold px-1 rounded-sm" data-id="element-4455" data-mp="36tl3rlb3p1fo">
                              ?
                            </div>
                          </div>}
                        <span className="text-gray-900" data-id="element-4456" data-mp="zzhb77p8xlbp">{row.fileName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4457" data-mp="v3s7qta3vka4">{row.site}</td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4458" data-mp="2cyj8bp7jwbrx">{row.subName}</td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4459" data-mp="2hynn9ast8yg1">{row.date}</td>
                    <td className="px-6 py-4" data-id="element-4460" data-mp="23yw2mr7sgjt2">
                      {row.status === 'Completed' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-4461" data-mp="28xfokrmdtvlf">
                          Completed
                        </span>}
                      {row.status === 'No Content' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700" data-id="element-4462" data-mp="2mxi44777wcxi">
                          No Content
                        </span>}
                      {row.status === 'Workers Signing' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-4463" data-mp="3fkb9j2vjy0du">
                          Workers Signing
                        </span>}
                    </td>
                  </tr>)}
                {filteredData.length === 0 && <tr data-id="element-4464" data-mp="3k9nglvuccb17">
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-4465" data-mp="3k93yof4oxmn6">
                      No PTPs found matching your criteria.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>;
}